<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { mapGetters, mapActions, mapState } from "vuex";
import moment from "moment";
import { ModelListSelect } from "vue-search-select";

export default {
  page: {
    title: "Factory Performance Report",
    meta: [{ name: "description", content: appConfig.description }]
  },

  components: {
    Layout,
    PageHeader,
    ModelListSelect
  },

  data() {
    return {
      title: "Factory Performance Report",
      items: [
        { text: "Home", href: "/" },
        { text: "Factory Performance Report", active: true }
      ],

      filterPerformance: {
        month: moment().month(),
        year: moment().year()
      },

      Months: [
        { id: 12, text: "All Months" },
        { id: 0, text: "January" },
        { id: 1, text: "February" },
        { id: 2, text: "March" },
        { id: 3, text: "April" },
        { id: 4, text: "May" },
        { id: 5, text: "June" },
        { id: 6, text: "July" },
        { id: 7, text: "August" },
        { id: 8, text: "September" },
        { id: 9, text: "October" },
        { id: 10, text: "November" },
        { id: 11, text: "December" }
      ],

      json_fields: {
        Date: "Date",
        "Input Qty": "Input Qty",
        "Output Qty": "Output Qty",
        Input: "Input",
        Output: "Output",
        Expenses: "Expenses",
        "Net Input": "Net Input",
        "Net Output": "Net Output",
        Actual: "Actual",
        "Net Actual": "Net Actual"
      },

      initialDataLoaded: false
    };
  },

  computed: {
    ...mapState("notification", ["type", "message", "show"]),
    ...mapGetters("production", ["factoryPerformance", "filterStatus"]),

    // ✅ SAFE TOTALS
    totals() {
      const list = Array.isArray(this.factoryPerformance)
        ? this.factoryPerformance
        : [];

      let t = {
        input_qty: 0,
        output_qty: 0,
        input: 0,
        output: 0,
        expenses: 0,
        net_input: 0,
        net_output: 0,
        actual: 0,
        net_actual: 0
      };

      list.forEach(i => {
        t.input_qty += +i.input_quantity || 0;
        t.output_qty += +i.output_quantity || 0;
        t.input += +i.input_factory_balance || 0;
        t.output += +i.output_factory_balance || 0;
        t.expenses += +i.expenses || 0;
        t.net_input += +i.net_input_factory_balance || 0;
        t.net_output += +i.net_output_factory_balance || 0;
        t.actual += +i.actual_factory_balance || 0;
        t.net_actual += +i.net_actual_factory_balance || 0;
      });

      return t;
    },

    // ✅ SAFE EXPORT DATA
    exportData() {
      const list = Array.isArray(this.factoryPerformance)
        ? this.factoryPerformance
        : [];

      let data = list.map(r => ({
        Date: r.period,
        "Input Qty": r.input_quantity,
        "Output Qty": r.output_quantity,
        Input: r.input_factory_balance,
        Output: r.output_factory_balance,
        Expenses: r.expenses,
        "Net Input": r.net_input_factory_balance,
        "Net Output": r.net_output_factory_balance,
        Actual: r.actual_factory_balance,
        "Net Actual": r.net_actual_factory_balance
      }));

      data.push({
        Date: "TOTAL",
        "Input Qty": this.totals.input_qty,
        "Output Qty": this.totals.output_qty,
        Input: this.totals.input,
        Output: this.totals.output,
        Expenses: this.totals.expenses,
        "Net Input": this.totals.net_input,
        "Net Output": this.totals.net_output,
        Actual: this.totals.actual,
        "Net Actual": this.totals.net_actual
      });

      return data;
    }
  },

  methods: {
    ...mapActions({
      fetchData: "production/fetchFactoryPerformance"
    }),

    thousandSeperator(n) {
      return Number(n || 0).toLocaleString();
    },

    async loadData() {
      this.initialDataLoaded = false;

      await this.fetchData(this.filterPerformance);

      this.initialDataLoaded = true;
    }
  },

  created() {
    this.loadData();
  }
};
</script>

<template>
<Layout>
  <PageHeader :title="title" :items="items" />

  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-body">

          <h3 class="text-center mb-3">Factory Performance Report</h3>

          <!-- FILTERS -->
          <div class="row mb-3">
            <div class="col-md-3">
              <label>Month</label>
              <model-list-select :list="Months" v-model="filterPerformance.month"
                option-value="id" option-text="text" @input="loadData" />
            </div>

            <div class="col-md-3">
              <label>Year</label>
              <input v-model.number="filterPerformance.year" type="number" class="form-control" />
            </div>

            <div class="col-md-3 mt-4">
              <button class="btn btn-primary" @click="loadData">
                <i class="fa fa-filter"></i> Filter
              </button>
            </div>

            <div class="col-md-3 mt-4">
              <download-excel
                class="btn btn-success"
                :data="exportData"
                :fields="json_fields"
                :worksheet="'Factory Performance'"
                type="xls"
              >
                Export Excel <i class="fa fa-file-excel"></i>
              </download-excel>
            </div>
          </div>

          <!-- LOADING -->
          <div v-if="filterStatus" class="text-center text-primary my-5">
            <b-spinner style="width: 3rem; height: 3rem;"></b-spinner>
          </div>

          <!-- TABLE -->
          <div v-else-if="initialDataLoaded && factoryPerformance.length > 0" class="table-responsive">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Input Qty</th>
                  <th>Output Qty</th>
                  <th>Input-Based Charge</th>
                  <th class="text-primary">Output-Based Charge</th>
                  <th>Actual Charge</th>
                  <th>Expenses</th>
                  <th>Input Factory Balance</th>
                  <th>Output Factory Balance</th>                  
                  <th>Actual Factory Balance</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(row, i) in factoryPerformance" :key="i">
                  <td>{{ row.period }}</td>
                  <td>{{ thousandSeperator(row.input_quantity) }}</td>
                  <td>{{ thousandSeperator(row.output_quantity) }}</td>
                  <td>{{ thousandSeperator(row.input_factory_balance) }}</td>
                  <td class="bg-primary text-white">{{ thousandSeperator(row.output_factory_balance) }}</td>
                  <td>{{ thousandSeperator(row.actual_factory_balance) }}</td>
                  <td>{{ thousandSeperator(row.expenses) }}</td>
                  <td>{{ thousandSeperator(row.net_input_factory_balance) }}</td>
                  <td>{{ thousandSeperator(row.net_output_factory_balance) }}</td>                  
                  <td>{{ thousandSeperator(row.net_actual_factory_balance) }}</td>
                </tr>
              </tbody>

              <tfoot>
                <tr class="fw-bold">
                  <td><b>TOTAL</b></td>
                  <td><b>{{ thousandSeperator(totals.input_qty) }}</b></td>
                  <td><b>{{ thousandSeperator(totals.output_qty) }}</b></td>
                  <td><b>{{ thousandSeperator(totals.input) }}</b></td>
                  <td class="bg-primary text-white"><b>{{ thousandSeperator(totals.output) }}</b></td>
                  <td><b>{{ thousandSeperator(totals.actual) }}</b></td>
                  <td><b>{{ thousandSeperator(totals.expenses) }}</b></td>
                  <td><b>{{ thousandSeperator(totals.net_input) }}</b></td>
                  <td><b>{{ thousandSeperator(totals.net_output) }}</b></td>                  
                  <td><b>{{ thousandSeperator(totals.net_actual) }}</b></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- EMPTY -->
          <div v-else-if="initialDataLoaded" class="text-center py-5">
            <h4>No Data Available</h4>
          </div>

        </div>
      </div>
    </div>
  </div>
</Layout>
</template>